import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Button,
} from "@mui/material";

const NewFileDialog = ({
  open,
  modules,
  newFileModule,
  newFileSide,
  newFileFolder,
  newFileName,
  newFileType,
  newFileError,
  newFileLoading,
  onClose,
  onChangeFileModule,
  onChangeFileSide,
  onChangeFileFolder,
  onChangeFileName,
  onChangeFileType,
  onCreateFile,
}) => {
  // NEW STATE for "Create new folder"
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderBase, setNewFolderBase] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderError, setNewFolderError] = useState("");

  const availableBaseFolders = [
    "",
    "routes",
    "services",
    "validators",
    "models",
    "utilities",
    "component",
    "utility",
    "other",
  ];

  const handleConfirmNewFolder = () => {
    if (!newFolderName.trim()) {
      setNewFolderError("Folder name is required.");
      return;
    }

    const base = newFolderBase.trim();
    const name = newFolderName.trim();

    const fullPath = base ? `${base}/${name}` : name;

    onChangeFileFolder(fullPath);
    setCreatingFolder(false);
    setNewFolderBase("");
    setNewFolderName("");
    setNewFolderError("");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Create new file</DialogTitle>
      <DialogContent dividers>
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel id="new-file-module-label">Module</InputLabel>
          <Select
            labelId="new-file-module-label"
            label="Module"
            value={newFileModule}
            onChange={(e) => onChangeFileModule(e.target.value)}
          >
            {modules.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="new-file-side-label">Side</InputLabel>
            <Select
              labelId="new-file-side-label"
              label="Side"
              value={newFileSide}
              onChange={(e) => onChangeFileSide(e.target.value)}
            >
              <MenuItem value="backend">Backend</MenuItem>
              <MenuItem value="frontend">Frontend</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="new-file-folder-label">Folder</InputLabel>
            <Select
              labelId="new-file-folder-label"
              label="Folder"
              value={newFileFolder}
              onChange={(e) => onChangeFileFolder(e.target.value)}
            >
              {availableBaseFolders.map((f) => (
                <MenuItem key={f} value={f}>
                  {f || "(module root)"}
                </MenuItem>
              ))}

              {/* NEW: support custom folder paths so the Select still shows a value */}
              {newFileFolder &&
                !availableBaseFolders.includes(newFileFolder) && (
                  <MenuItem key={newFileFolder} value={newFileFolder}>
                    {newFileFolder}
                  </MenuItem>
                )}
            </Select>
          </FormControl>
        </Stack>

        {/* NEW: Button to create a folder */}
        <Button
          size="small"
          sx={{ mt: 1 }}
          onClick={() => setCreatingFolder(true)}
        >
          + Create new folder
        </Button>

        {/* NEW: Inline UI for creating the folder */}
        {creatingFolder && (
          <Stack spacing={1} sx={{ mt: 1, p: 1, border: "1px solid #ccc", borderRadius: 1 }}>
            <Typography variant="subtitle2">New folder</Typography>

            <FormControl fullWidth size="small">
              <InputLabel id="new-folder-base-label">Base folder</InputLabel>
              <Select
                labelId="new-folder-base-label"
                label="Base folder"
                value={newFolderBase}
                onChange={(e) => setNewFolderBase(e.target.value)}
              >
                {availableBaseFolders.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f || "(module root)"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="New folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
            />

            {newFolderError && (
              <Typography variant="caption" color="error">
                {newFolderError}
              </Typography>
            )}

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                onClick={handleConfirmNewFolder}
              >
                Add
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setCreatingFolder(false);
                  setNewFolderBase("");
                  setNewFolderName("");
                  setNewFolderError("");
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        )}

        <TextField
          margin="dense"
          label="File name (no extension)"
          fullWidth
          value={newFileName}
          onChange={(e) => onChangeFileName(e.target.value)}
          sx={{ mt: 2 }}
        />

        <TextField
          margin="dense"
          label="File type (optional: route/service/validator/component/other)"
          fullWidth
          value={newFileType}
          onChange={(e) => onChangeFileType(e.target.value)}
          sx={{ mt: 1 }}
        />

        {newFileError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 1 }}
          >
            {newFileError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={newFileLoading}>
          Cancel
        </Button>
        <Button
          onClick={onCreateFile}
          variant="contained"
          disabled={newFileLoading}
        >
          {newFileLoading ? "Creating..." : "Create file"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewFileDialog;
